import { useRef, useState, useCallback, RefObject } from 'react';
import { errorReporter } from '@/services/errorReportingService';

interface UseThumbnailPreviewOptions {
  videoRef: RefObject<HTMLVideoElement>;
  progressRef: RefObject<HTMLInputElement>;
}

interface UseThumbnailPreviewReturn {
  // Refs
  thumbnailVideoRef: RefObject<HTMLVideoElement>;
  snapshotRef: RefObject<HTMLDivElement>;
  // State
  snapshots: string;
  showThumbnail: boolean;
  // Actions
  onSliderHover: (e: React.MouseEvent<HTMLInputElement>) => void;
  onOutsideSlider: () => void;
  setThumbnailMetadataLoaded: () => void;
}

export function useThumbnailPreview(options: UseThumbnailPreviewOptions): UseThumbnailPreviewReturn {
  const { videoRef, progressRef } = options;

  // Refs
  const thumbnailVideoRef = useRef<HTMLVideoElement>(null);
  const snapshotRef = useRef<HTMLDivElement>(null);
  const reusableCanvas = useRef<HTMLCanvasElement | null>(null);

  // State
  const [snapshots, setSnapshots] = useState('');
  const [showThumbnail, setShowThumbnail] = useState<boolean>(false);

  // Capture frame from video
  const capture = useCallback((video: HTMLVideoElement) => {
    if (!reusableCanvas.current) {
      reusableCanvas.current = document.createElement('canvas');
    }
    const canvas = reusableCanvas.current;
    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx?.clearRect(0, 0, canvas.width, canvas.height);
    ctx?.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
    return canvas;
  }, []);

  // Take a snapshot - returns true if successful, false if failed due to security restrictions
  const shoot = useCallback(
    (video: HTMLVideoElement): boolean => {
      const canvas = capture(video);
      try {
        // toDataURL() throws SecurityError on tainted canvas (cross-origin without CORS)
        // This is standard browser security behavior across all modern browsers
        setSnapshots(canvas.toDataURL());
        canvas.remove();
        return true;
      } catch (error: unknown) {
        // Handle SecurityError from tainted canvas - allows graceful degradation
        // Thumbnail preview will be disabled but video playback continues normally
        if (error instanceof DOMException && error.name === 'SecurityError') {
          errorReporter.expected('Canvas tainted by cross-origin video - thumbnail preview disabled', error, {
            feature: 'video',
            operation: 'useThumbnailPreview.shoot',
          });
          setSnapshots('');
          canvas.remove();
          return false;
        } else {
          // Re-throw unexpected errors so they can be caught by error boundaries
          canvas.remove();
          throw error;
        }
      }
    },
    [capture],
  );

  // Handle slider hover for thumbnail preview
  const onSliderHover = useCallback(
    (e: React.MouseEvent<HTMLInputElement>) => {
      if (videoRef.current && thumbnailVideoRef.current && progressRef.current) {
        const progressBar = progressRef.current;

        const rect = progressBar.getBoundingClientRect();
        const relativeX = e.clientX - rect.left;
        const percent = Math.min(Math.max(relativeX / rect.width, 0), 1);

        const hoverTime = percent * (videoRef.current.duration || 0);

        // Prevent crashes from malformed video metadata
        if (!Number.isFinite(hoverTime) || !Number.isFinite(videoRef.current.duration)) {
          return;
        }

        thumbnailVideoRef.current.currentTime = hoverTime;

        const success = shoot(thumbnailVideoRef.current);

        // Only show thumbnail if snapshot was successfully captured
        if (success) {
          if (snapshotRef.current) {
            const leftPosition = relativeX;
            snapshotRef.current.style.left = `${leftPosition}px`;
          }
          setShowThumbnail(true);
        } else {
          // Hide thumbnail if capture failed (e.g., cross-origin security restriction)
          setShowThumbnail(false);
        }
      }
    },
    [progressRef, shoot, videoRef],
  );

  // Handle mouse leaving slider
  const onOutsideSlider = useCallback(() => {
    setSnapshots('');
    setShowThumbnail(false);
  }, []);

  // Callback for thumbnail video metadata loaded
  const setThumbnailMetadataLoaded = useCallback(() => {
    // This is a no-op callback that can be used if needed
    // The main video's metadata loading is handled in useVideoPlayback
  }, []);

  return {
    // Refs
    thumbnailVideoRef,
    snapshotRef,
    // State
    snapshots,
    showThumbnail,
    // Actions
    onSliderHover,
    onOutsideSlider,
    setThumbnailMetadataLoaded,
  };
}
